import {
    UserDefinedNotificationConfig,
    UserDefinedNotificationConfigCooldownPeriod,
    UserDefinedNotificationRecipient,
    UserDefinedNotificationRecipientMessageTypeItem,
} from '@apis/Notification/model';
import styled from '@emotion/styled';
import { ActionIcon, Divider, Group, Switch, Tooltip, useMantineTheme } from '@mantine/core';
import { useCurrentUser } from '@root/Components/Users/UserHooks';
import { InfoIconTooltip } from '@root/Design/Primitives';
import {
    SettingsInputRow,
    SettingsInteractiveRow,
    SettingsLabel,
    SettingsSection,
    SettingsSectionItem,
    SettingsSectionItemBody,
    SettingsSectionItemHeader,
    SettingsSectionItemHeaderLabel,
} from '@root/Design/Settings';
import { UserRecord } from '@root/Services/Customers/CompanyUserService';
import { EventEmitter, useEvent } from '@root/Services/EventEmitter';
import { MouseEventHandler, ReactNode, useCallback, useMemo } from 'react';
import { BellZ, ListCheck, Mail, Plus } from 'tabler-icons-react';
import { UserPicker } from '../../Users/UserPicker';
import { RecurrenceIncrement, RecurrenceTypeSelector } from '../Components/Schedule';
import { useInputBinding } from '@root/Components/Settings/SettingsInputs';

type MessageType = UserDefinedNotificationRecipientMessageTypeItem;
const getAllMessageTypes = () => ['InApp', 'Email'] as MessageType[];
type SelectableMessageType = { value: MessageType; label: string; icon: ReactNode; selected: boolean; toggle: () => void };

interface IDeliveryOptionsProps {
    definition: UserDefinedNotificationConfig;
    recipients: UserDefinedNotificationRecipient[];
    typePrefId: number | undefined;
    ownerUserId: number;
    onChanged?: () => void;
}
export function DeliveryOptions(props: IDeliveryOptionsProps) {
    const { ownerUserId, recipients, typePrefId, definition } = props;
    const currentUser = useCurrentUser();
    const isCurrentUserOwner = currentUser?.Id === ownerUserId;

    const currentUserRecipient = useMemo(() => {
        let currentUserRecipient = recipients.find((r) => r.UserId === currentUser?.Id);
        if (!currentUserRecipient) {
            currentUserRecipient = {
                NotificationTypePreferenceId: typePrefId,
                UserId: currentUser?.Id,
                MessageType: !typePrefId ? getAllMessageTypes() : [],
                Type: 'SingleInclude',
            } as UserDefinedNotificationRecipient;
            recipients.push(currentUserRecipient);
        }
        return currentUserRecipient;
    }, [recipients]);

    return (
        <SettingsSection title="Delivery Options">
            <UserPreferenceSection {...props} currentUserRecipient={currentUserRecipient} />
            {!isCurrentUserOwner ? null : <OtherRecipientsSection {...props} currentUserRecipient={currentUserRecipient} />}
        </SettingsSection>
    );
}

interface IDeliveryOptionsSectionProps extends IDeliveryOptionsProps {
    currentUserRecipient: UserDefinedNotificationRecipient;
}

const typeMaxIncrement = new Map<UserDefinedNotificationConfigCooldownPeriod, number>([
    ['Daily', 999],
    ['Weekly', 150],
    ['Monthly', 30],
]);
type Period = 'Daily' | 'Weekly' | 'Monthly';

function UserPreferenceSection(props: IDeliveryOptionsSectionProps) {
    const { colors } = useMantineTheme();
    const { currentUserRecipient, onChanged, definition } = props;
    const selectionChanged = useMemo(() => EventEmitter.empty(), []);
    const increment = useInputBinding(props.definition, (o) => o.CooldownIncrement!, 1);
    const period = useInputBinding(props.definition, (o) => o.CooldownPeriod as Period, 'Daily');
    const max = typeMaxIncrement.get(period.value);

    const handleChange = useCallback(() => {
        onChanged?.();
        selectionChanged.emit();
    }, [onChanged, selectionChanged]);
    const { availableMessageTypes, changedEvent } = useMessageTypePickerController(currentUserRecipient, handleChange);
    useEvent(changedEvent);

    return (
        <>
            <SettingsSectionItem>
                <SettingsSectionItemHeader>
                    <SettingsSectionItemHeaderLabel>Preference</SettingsSectionItemHeaderLabel>
                    <InfoIconTooltip label="Choose how you'd like to receive this notification." />
                </SettingsSectionItemHeader>
                <SettingsSectionItemBody>
                    {availableMessageTypes.map(({ icon, label, selected, toggle }, i) => (
                        <SettingsInteractiveRow key={i} onClick={toggle}>
                            <SettingsLabel strokeWidth={selected ? 2 : 1} iconColor={selected ? colors.primary[5] : undefined} icon={icon}>
                                {label}
                            </SettingsLabel>
                            <Switch
                                readOnly
                                checked={selected}
                                sx={{
                                    input: { cursor: 'pointer' },
                                }}
                                color="primary.5"
                                size="xs"
                                radius={3}
                                onChange={() => {}}
                            />
                        </SettingsInteractiveRow>
                    ))}
                    {definition.Trigger?.TriggerType == 'Threshold' && (
                        <SettingsInputRow>
                            <SettingsLabel strokeWidth={2} iconColor={colors.primary[5]} icon={<BellZ />}>
                                Resend Every
                            </SettingsLabel>
                            <Tooltip
                                withinPortal
                                multiline
                                width={300}
                                label="Select how often the alert should be resent while the alert conditions are met. e.g. if you select 1 and Daily, you'll receive the alert every day while the conditions are met, but not more than once a day."
                            >
                                <Group spacing={6} noWrap>
                                    <RecurrenceIncrement {...increment} max={max ?? 1} />
                                    <RecurrenceTypeSelector {...period} increment={increment.value} />
                                </Group>
                            </Tooltip>
                        </SettingsInputRow>
                    )}
                </SettingsSectionItemBody>
            </SettingsSectionItem>
        </>
    );
}

function OtherRecipientsSection(props: IDeliveryOptionsSectionProps) {
    const { currentUserRecipient, onChanged, recipients, typePrefId } = props;
    const selectionChanged = useMemo(() => EventEmitter.empty(), []);
    const handleChange = useCallback(() => {
        onChanged?.();
        selectionChanged.emit();
    }, [onChanged, selectionChanged]);
    useEvent(selectionChanged);

    const { onAdd, onDelete, onClear, noop, selectedUserIds, userPrefLookup } = useMemo(() => {
        const userPrefLookup = new Map(recipients.map((r) => [r.UserId, r] as [number, UserDefinedNotificationRecipient]));

        return {
            userPrefLookup,
            noop: () => {},
            onAdd: (userId: number) => {
                if (!userPrefLookup.has(userId)) {
                    userPrefLookup.set(userId, { NotificationTypePreferenceId: typePrefId ?? 0, UserId: userId, MessageType: getAllMessageTypes() });
                    recipients.push(userPrefLookup.get(userId)!);
                    handleChange();
                }
            },
            onDelete: (userId: number) => {
                const recipient = userPrefLookup.get(userId);
                if (recipient) {
                    recipients.splice(recipients.indexOf(recipient), 1);
                    userPrefLookup.delete(userId);
                    handleChange();
                }
            },
            onClear: () => {
                const currentUserId = currentUserRecipient.UserId;
                const toRemove = recipients.filter((r) => r.UserId !== currentUserId);

                toRemove.forEach((r) => {
                    userPrefLookup.delete(r.UserId!);
                    const index = recipients.indexOf(r);
                    if (index !== -1) recipients.splice(index, 1);
                });

                handleChange();
            },
            get selectedUserIds() {
                return [...userPrefLookup.values()].filter((r) => !!r.MessageType?.length).map((r) => r.UserId!);
            },
        };
    }, [recipients, handleChange]);

    const OtherRecipientItem = useMemo(() => {
        return function OtherRecipientItem({ user }: { user: UserRecord }) {
            useEvent(selectionChanged);
            const recipient = userPrefLookup.get(user.Id);
            const handleAdd = useCallback(() => onAdd(user.Id), [user.Id]);
            const handleDelete = useCallback(() => onDelete(user.Id), [user.Id]);
            return (
                <>
                    {!recipient ? (
                        <OtherRecipientAdd onClick={handleAdd} />
                    ) : (
                        <OtherRecipientPreference recipient={recipient} onChanged={onChanged} onEmpty={handleDelete} />
                    )}
                </>
            );
        };
    }, [onChanged, selectionChanged, userPrefLookup]);
    const renderOthersPreferences = useCallback((user: UserRecord) => <OtherRecipientItem user={user} />, [OtherRecipientItem]);

    return (
        <SettingsSectionItem>
            <SettingsSectionItemHeader>
                <SettingsSectionItemHeaderLabel>Other Recipients</SettingsSectionItemHeaderLabel>
                <InfoIconTooltip label="Others in your organization may receive your alerts and scheduled reports. Add or remove delivery options for other recipients using the list below. They may choose to opt-out at any time." />
            </SettingsSectionItemHeader>
            <SettingsSectionItemBody style={{ padding: 0 }}>
                <PickerStyles>
                    <UserPicker
                        excludeCurrentUser
                        selectedUserIds={selectedUserIds}
                        onChange={noop}
                        controlled
                        itemRightSection={renderOthersPreferences}
                        height={250}
                        disableAutoFocus
                        onClear={onClear}
                        rememberSortPreference={true}
                        sortPreferenceKey="NotificationRecipientSort"
                    />
                </PickerStyles>
            </SettingsSectionItemBody>
        </SettingsSectionItem>
    );
}

const PickerStyles = styled.div`
    display: content;
    --picker-item-selected-bg: transparent;
    --picker-item-hover-bg: ${(p) => p.theme.colors.primary[2]};
    --picker-item-hover-selected-bg: ${(p) => p.theme.colors.primary[2]};
    .picker-item {
    }
    .add-other-item {
        width: 0;
        overflow: hidden;
    }
    .picker-item:hover {
        .add-other-item {
            width: auto;
        }
    }
    .picker-item-text {
        padding-left: 0;
    }
    .picker-item-checkbox {
        width: 0;
        display: none;
    }
`;

function OtherRecipientPreference(props: { recipient: UserDefinedNotificationRecipient; onEmpty: () => void; onChanged?: () => void }) {
    const { recipient, onChanged, onEmpty } = props;
    const handleContainerClick: MouseEventHandler<HTMLDivElement> = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
    }, []);

    const onChange = useCallback(() => {
        if (!recipient.MessageType?.length) {
            onEmpty();
        }
        onChanged?.();
    }, [onChanged, onEmpty, recipient]);
    const { changedEvent, availableMessageTypes } = useMessageTypePickerController(recipient, onChange);
    useEvent(changedEvent);

    return (
        <>
            <Divider orientation="vertical" />
            <Group noWrap onClick={handleContainerClick}>
                {availableMessageTypes.map(({ icon, label, selected, toggle }, i) => (
                    <Tooltip withinPortal key={i} label={`${label}: ${selected ? 'Enabled' : 'Disabled'}`}>
                        <ActionIcon component={PreferenceIcon} selected={selected} onClick={toggle}>
                            {icon}
                        </ActionIcon>
                    </Tooltip>
                ))}
            </Group>
        </>
    );
}
function OtherRecipientAdd({ onClick }: { onClick?: () => void }) {
    return (
        <>
            <Divider orientation="vertical" />
            <Group noWrap className="add-other-item">
                <Tooltip withinPortal label="Add Recipient">
                    <ActionIcon component={PreferenceIcon} selected onClick={onClick}>
                        <Plus />
                    </ActionIcon>
                </Tooltip>
            </Group>
        </>
    );
}

const PreferenceIcon = styled.div<{ selected: boolean }>`
    color: ${(p) => (p.selected ? p.theme.colors.primary[5] : p.theme.colors.gray[6])};
    svg {
        stroke-width: ${(p) => (p.selected ? 2 : 1)}px;
        width: 22px;
        height: 22px;
    }
`;

function useMessageTypePickerController(recipient: UserDefinedNotificationRecipient, onChange?: () => void) {
    const result = useMemo(() => {
        const changedEvent = EventEmitter.empty();
        const types = (recipient.MessageType ??= []);
        const baseTypes = [
            {
                value: 'InApp' as const,
                label: 'In-App Notifications',
                icon: <ListCheck />,
            },
            {
                value: 'Email' as const,
                icon: <Mail />,
                label: 'Email Notifications',
            },
        ];

        return {
            changedEvent,
            availableMessageTypes: baseTypes.map((item) => ({
                ...item,
                get selected() {
                    return types.includes(item.value);
                },
                toggle: () => {
                    const index = types.findIndex((t) => t === item.value);
                    if (index >= 0) {
                        types.splice(index, 1);
                    } else {
                        types.push(item.value);
                    }
                    onChange?.();
                    changedEvent.emit();
                },
            })) as Array<SelectableMessageType>,
        };
    }, [recipient, onChange]);

    return result;
}
